import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, UsersIcon, SettingsIcon, BarChart3Icon, LandmarkIcon, PlaneIcon, GraduationCapIcon, HeartPulseIcon, RadioTowerIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { ecosystemsIntro, ecosystems, ecosystemCta, ecosystemsClosing, type Bilingual, type Ecosystem } from '../data/ecosystemsContent';
import { trackEvent as track } from '../utils/analytics';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: EASE
  }
};
const DOT_FIELD: React.CSSProperties = {
  backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px), radial-gradient(currentColor 1px, transparent 1px)',
  backgroundSize: '18px 18px, 26px 26px',
  backgroundPosition: '0 0, 9px 13px'
};
const SKYLINE = 'https://cdn.magicpatterns.com/patterns/generated-images/c6ee049f-39b3-4551-afd5-3078898be628.jpg';
const SECTOR_ICONS: Record<Ecosystem['icon'], typeof LandmarkIcon> = {
  city: LandmarkIcon,
  plane: PlaneIcon,
  campus: GraduationCapIcon,
  health: HeartPulseIcon,
  telecom: RadioTowerIcon
};
const BENEFIT_ICONS = {
  people: UsersIcon,
  gear: SettingsIcon,
  chart: BarChart3Icon
};
export function Ecosystems() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (value: Bilingual) => isFR ? value.fr : value.en;
  return <section id="communities" className="bg-white border-t border-[#E4E6EC] py-16 lg:py-20 px-4 sm:px-6" data-id="element-3466" data-mp="2rjui842re4n">
      <div className="max-w-[1320px] mx-auto" data-id="element-3467" data-mp="3tye9zazj0l59">
        {/* Header + context */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-8 lg:gap-10 items-start" data-id="element-3468" data-mp="1x5azgubgl9tr">
          <motion.div {...reveal} data-id="element-3469" data-mp="1s684y3t3um01">
            <div className="flex items-center gap-3" data-id="element-3470" data-mp="1s712qfa36161">
              <span className="w-6 h-px bg-[#4F46E5]" aria-hidden="true" data-id="element-3471" data-mp="2bal2ufygy3px" />
              <p className="font-sans text-xs font-semibold tracking-[0.22em] text-[#4F46E5] uppercase" data-id="element-3472" data-mp="2b9a10lol361g">
                {pick(ecosystemsIntro.eyebrow)}
              </p>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.14] tracking-tight mt-4" data-id="element-3473" data-mp="228bv1xul4og8">
              <span className="block text-[#121212]" data-id="element-3474" data-mp="277f0vkie2yp9">
                {pick(ecosystemsIntro.headlineLead)}
              </span>
              <span className="block text-[#4F46E5]" data-id="element-3475" data-mp="2z0fc6njkxa5f">
                {pick(ecosystemsIntro.headlineAccent)}
              </span>
            </h2>
            <p className="font-sans text-base text-[#5B6478] leading-relaxed mt-5 max-w-xl" data-id="element-3476" data-mp="2l0ec3ab526r0">
              {pick(ecosystemsIntro.intro)}
            </p>
          </motion.div>

          <motion.div {...reveal} transition={{
          duration: 0.6,
          delay: 0.08,
          ease: EASE
        }} className="relative rounded-2xl overflow-hidden" data-id="element-3477" data-mp="19lhiro7x8vdu">
            <img src={SKYLINE} alt="" aria-hidden="true" className="w-full h-[230px] lg:h-[250px] object-cover" data-id="element-3478" data-mp="170tarxqelmet" />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/55 to-white/10" aria-hidden="true" data-id="element-3479" data-mp="1g1r85x6c6vof" />
            <div className="absolute inset-0 bg-gradient-to-t from-white/75 to-transparent" aria-hidden="true" data-id="element-3480" data-mp="1b3popb500ucf" />

            <div className="absolute inset-0 flex items-start justify-between gap-4 p-5" data-id="element-3481" data-mp="jca3nlj3bfob">
              <div className="max-w-[45%]" data-id="element-3482" data-mp="x2vhuwxln1fv">
                <span className="block w-8 h-px bg-[#4F46E5] mb-3" aria-hidden="true" data-id="element-3483" data-mp="x4pyw05r29eo" />
                <p className="font-sans text-[10px] lg:text-[11px] font-semibold tracking-[0.16em] uppercase text-[#5B6478] leading-relaxed" data-id="element-3484" data-mp="4mugefirpsdd">
                  {pick(ecosystemsIntro.context)}
                </p>
              </div>

              <ul className="space-y-2.5" data-id="element-3485" data-mp="db9lizq4rl54">
                {ecosystemsIntro.indicators.map(indicator => {
                const Icon = BENEFIT_ICONS[indicator.icon];
                return <li key={indicator.label.en} className="flex items-center gap-2.5" data-id="element-3486" data-mp="n7mnslzqr7bh">
                      <span className="w-9 h-9 shrink-0 rounded-full bg-white shadow-[0_1px_3px_rgba(18,18,18,0.12)] flex items-center justify-center" data-id="element-3487" data-mp="s17eru2x3116">
                        <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-3488" data-mp="10yrf90sygtaq" />
                      </span>
                      <span className="font-sans text-xs text-[#121212] leading-snug max-w-[8.5rem]" data-id="element-3489" data-mp="1aw5tctrep5zv">
                        {pick(indicator.label)}
                      </span>
                    </li>;
              })}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Five ecosystems */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mt-12" data-id="element-3490" data-mp="1p38xxf69frwn">
          {ecosystems.map((sector, index) => {
          const Icon = SECTOR_ICONS[sector.icon];
          return <motion.div key={sector.id} {...reveal} transition={{
            duration: 0.6,
            delay: index * 0.06,
            ease: EASE
          }} data-id="element-3491" data-mp="1g4dmnooczrmh" data-mpi={index}>
                <Link to={sector.to} onClick={() => track('Industry Tile Clicked', {
              industry: sector.title.en
            })} className="group h-full flex flex-col rounded-2xl border border-[#E4E6EC] bg-white shadow-[0_1px_3px_rgba(18,18,18,0.05)] overflow-hidden transition-colors duration-200 hover:border-[#4F46E5]/40" data-id="element-3492" data-mp="28rhus2ir0m2h">
                  <div className="relative" data-id="element-3493" data-mp="nctzxusjzvfq">
                    <img src={sector.image} alt={pick(sector.imageAlt)} className="w-full h-[150px] object-cover" data-id="element-3494" data-mp="ovkhblkv6f6t" />
                    <span className="absolute -bottom-5 left-4 w-11 h-11 rounded-full bg-white shadow-[0_2px_8px_rgba(18,18,18,0.12)] flex items-center justify-center" data-id="element-3495" data-mp="fv5w0m7wh03b">
                      <Icon className="w-[1.15rem] h-[1.15rem] text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-3496" data-mp="kw3dsxozzs4a" />
                    </span>
                  </div>

                  <div className="flex flex-col flex-1 px-5 pt-9 pb-5" data-id="element-3497" data-mp="3tatrq7cn9cpl">
                    <h3 className="font-serif text-xl text-[#121212] leading-snug" data-id="element-3498" data-mp="15hubjvt3zf2">
                      {pick(sector.title)}
                    </h3>
                    <p className="font-sans text-sm text-[#5B6478] mt-2" data-id="element-3499" data-mp="3jceaqx7bzjtt">
                      {pick(sector.descriptors)}
                    </p>
                    <span className="mt-auto pt-5 inline-flex items-center gap-2 font-sans text-sm font-medium text-[#4F46E5]" data-id="element-3500" data-mp="34ody44xqumjn">
                      {pick(ecosystemCta)}
                      <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-3501" data-mp="2zs749aimh1i2" />
                    </span>
                  </div>
                </Link>
              </motion.div>;
        })}
        </div>

        {/* Closing foundation panel */}
        <motion.div {...reveal} transition={{
        duration: 0.6,
        delay: 0.08,
        ease: EASE
      }} className="rounded-2xl bg-[#4F46E5]/[0.045] px-6 py-9 lg:px-10 mt-6" data-id="element-3502" data-mp="3imm3n18uiw6f">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_auto] gap-8 lg:gap-10 items-center" data-id="element-3503" data-mp="3sgl29q9pnxtm">
            <div data-id="element-3504" data-mp="67a45w697jp6">
              <p className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase" data-id="element-3505" data-mp="67til2wp3pyg">
                {pick(ecosystemsClosing.eyebrow)}
              </p>
              <p className="font-serif text-2xl lg:text-[1.9rem] leading-[1.2] mt-3" data-id="element-3506" data-mp="1colzeuuqw3ru">
                {ecosystemsClosing.statement.map(line => <span key={line.en} className="block text-[#121212]" data-id="element-3507" data-mp="1mlqlkuolbz8n">
                    {pick(line)}
                  </span>)}
                <span className="block text-[#4F46E5]" data-id="element-3508" data-mp="1qjo8fquw298v">
                  {pick(ecosystemsClosing.statementAccent)}
                </span>
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:divide-x lg:divide-[#4F46E5]/15 lg:border-l lg:border-[#4F46E5]/15 lg:pl-8" data-id="element-3509" data-mp="yv2xozya4z0g">
              {ecosystemsClosing.benefits.map(benefit => {
              const Icon = BENEFIT_ICONS[benefit.icon];
              return <div key={benefit.title.en} className="lg:px-4 first:lg:pl-0" data-id="element-3510" data-mp="b4qs3vqibed1">
                    <span className="w-11 h-11 rounded-full bg-white flex items-center justify-center" data-id="element-3511" data-mp="f5j42wqrjxd0">
                      <Icon className="w-[1.15rem] h-[1.15rem] text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-3512" data-mp="7m1eb1mxeb80" />
                    </span>
                    <h3 className="font-sans text-sm font-semibold text-[#121212] mt-3" data-id="element-3513" data-mp="3utvciqfjsnnq">
                      {pick(benefit.title)}
                    </h3>
                    <p className="font-sans text-sm text-[#5B6478] leading-snug mt-1.5" data-id="element-3514" data-mp="3kumosfba059t">
                      {pick(benefit.body)}
                    </p>
                  </div>;
            })}
            </div>

            <Link to="/request-demo" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#4F46E5] text-white px-7 py-3.5 font-sans text-sm font-medium hover:bg-[#4338CA] transition-colors duration-200 shrink-0" data-id="element-3515" data-mp="3b1yvhuji8jfu">
              {pick(ecosystemsClosing.cta)}
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-3516" data-mp="3b13a6o7n7o5b" />
            </Link>
          </div>
        </motion.div>

        {/* Closing statement */}
        <motion.div {...reveal} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: EASE
      }} className="relative overflow-hidden mt-10 py-6 text-center" data-id="element-3517" data-mp="36yzwru1ey9he">
          <span className="pointer-events-none absolute -left-24 -bottom-24 w-80 h-80 rounded-full text-[#4F46E5]/25 opacity-30" style={DOT_FIELD} aria-hidden="true" data-id="element-3518" data-mp="3ft4k43hc92s6" />
          <span className="pointer-events-none absolute -right-24 -bottom-24 w-80 h-80 rounded-full text-[#4F46E5]/25 opacity-30" style={DOT_FIELD} aria-hidden="true" data-id="element-3519" data-mp="2o2gmh1evifj9" />
          <div className="relative flex items-center justify-center gap-4" data-id="element-3520" data-mp="2x3eoxf9nyezq">
            <span className="hidden sm:block w-16 h-px bg-[#4F46E5]/25" aria-hidden="true" data-id="element-3521" data-mp="h8mlgftppen3" />
            <p className="font-sans text-[11px] tracking-[0.18em] uppercase text-[#5B6478] leading-relaxed" data-id="element-3522" data-mp="cal2duwadfbm">
              {pick(ecosystemsClosing.footer)}
            </p>
            <span className="hidden sm:block w-16 h-px bg-[#4F46E5]/25" aria-hidden="true" data-id="element-3523" data-mp="1z82pgc1vt8zr" />
          </div>
        </motion.div>
      </div>
    </section>;
}